import type {
  AddressStepResponse,
  BasicInfoStepResponse,
  FundingAccountResponse,
  MembershipInvitationResponse,
  MembershipResponse,
  PayScheduleResponse,
  WorkLocationResponse,
} from '../types';

/**
 * Development/QA-only synthetic fixture generator for MockBusinessProfileSetupGateway (VITE_USE_MOCKS=true
 * only — see BusinessProfileSetupGateway.ts). One full "already-configured company" bundle is generated PER
 * active CompanyId rather than a single global fixture — see MockBusinessProfileSetupGateway.ts's own doc
 * comment for why: Company Details must reflect whichever company the signed-in Admin currently has active
 * (identityGateway.getMe().activeCompanyId), not one hardcoded company regardless of context. Every value
 * here is deliberately fake and derived only from the real (mock) company's own id/name — a made-up-but-
 * stable phone/FEIN/address/bank, never anything that could be mistaken for real business or banking data
 * (see CLAUDE.md's "never introduce client-specific hard-coding" and "never expose ... full bank account
 * numbers" rules; this fixture never even holds a raw account/routing number in memory, matching the real
 * backend's own guarantee).
 */

export interface MockCompanyFixture {
  basicInfo: Omit<BasicInfoStepResponse, 'draft'>;
  address: Omit<AddressStepResponse, 'draft'>;
  fundingAccount: FundingAccountResponse;
  paySchedule: PayScheduleResponse;
  workLocations: WorkLocationResponse[];
  members: MembershipResponse[];
  invitations: MembershipInvitationResponse[];
}

const FICTIONAL_CITIES: { city: string; state: string; zip: string; county: string }[] = [
  { city: 'Springfield', state: 'IL', zip: '62704', county: 'Sangamon' },
  { city: 'Franklin', state: 'TX', zip: '76856', county: 'Robertson' },
  { city: 'Georgetown', state: 'CO', zip: '80444', county: 'Clear Creek' },
  { city: 'Fairview', state: 'OH', zip: '44126', county: 'Cuyahoga' },
];

const FICTIONAL_BANKS = ['Demo Community Bank', 'Placeholder National Bank', 'Sample Trust & Savings', 'Fixture Federal Credit Union'];

const FICTIONAL_INVITEES = ['Jordan Lee', 'Taylor Brooks', 'Morgan Reyes', 'Casey Winters'];

/** Step 5 fields every fixture location starts with: Active for Payroll, no separate mailing address, and no tax registration on file
 * (reported as Not Registered, never as complete). The main location overrides the registration below. */
const STEP5_DEFAULTS = {
  activeForPayroll: true,
  version: 0,
  mailingAddress: { sameAsPhysical: true, addressLine1: null, addressLine2: null, city: null, state: null, zip: null, country: null },
  taxRegistration: null,
  taxRegistrationStatus: 'NotRegistered' as const,
  registrationComplete: false,
  registrationIssues: ['Payroll tax registration is not on file for this location.'],
};

/** Deterministic, non-cryptographic hash — same companyId always produces the same synthetic fixture
 * within a session, and different companies produce visibly different values (see this module's own doc
 * comment on why per-company variation matters here). */
function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'company';
}

export function buildMockCompanyFixture(companyId: string, companyName: string): MockCompanyFixture {
  const hash = hashString(companyId);
  const slug = slugify(companyName);
  const primary = FICTIONAL_CITIES[hash % FICTIONAL_CITIES.length]!;
  const secondary = FICTIONAL_CITIES[(hash + 1) % FICTIONAL_CITIES.length]!;
  const bankName = FICTIONAL_BANKS[hash % FICTIONAL_BANKS.length]!;
  const inviteeName = FICTIONAL_INVITEES[hash % FICTIONAL_INVITEES.length]!;
  const last4 = String(hash % 10000).padStart(4, '0');
  const fein = String((hash % 900000000) + 100000000);
  const phoneSuffix = String(hash % 10000).padStart(4, '0');
  const phone = `+1555555${phoneSuffix}`;

  const basicInfo: Omit<BasicInfoStepResponse, 'draft'> = {
    isComplete: true,
    name: companyName,
    fein,
    email: `admin@${slug}.example`,
    phone,
    sein: `A-${String(hash % 10000000).padStart(7, '0')}`,
    website: `https://${slug}.example`,
  };

  const address: Omit<AddressStepResponse, 'draft'> = {
    isComplete: true,
    addressLine1: `${100 + (hash % 900)} Demo Plaza`,
    addressLine2: 'Suite 300',
    city: primary.city,
    state: primary.state,
    zip: primary.zip,
    country: 'US',
    county: primary.county,
    mailingSameAsBilling: true,
    mailingAddressLine1: null,
    mailingAddressLine2: null,
    mailingCity: null,
    mailingState: null,
    mailingZip: null,
    mailingCountry: null,
    mailingCounty: null,
  };

  /** Masked only, exactly like the real FundingAccountResponse — this fixture never holds a raw routing or
   * account number, not even in memory. */
  const fundingAccount: FundingAccountResponse = {
    id: `mock-funding-account-${slug}`,
    bankName,
    maskedAccountLast4: last4,
    accountType: 'Checking',
    fundingPartner: 'NatPay',
    verificationState: 'Verified',
  };

  const paySchedule: PayScheduleResponse = {
    id: `mock-pay-schedule-${slug}`,
    companyId,
    name: 'Company Pay Schedule',
    frequency: 2, // Biweekly
    firstPayDay: 5, // Friday
    firstPayDayKind: 1,
    secondPayDay: null,
    secondPayDayKind: null,
    payPeriodStartDate: '2026-01-02',
    isDefault: true,
    isActive: true,
    createdAtUtc: '2026-01-01T00:00:00Z',
    updatedAtUtc: null,
    firstPayDate: null,
  };

  /** Main location — mirrors the real backend's own rule (derived from Company Address, LocationType
   * always null). One additional active branch, plus one archived location, to exercise both the
   * Active/Archived sections and the Reactivate action. */
  const workLocations: WorkLocationResponse[] = [
    {
      id: `mock-location-${slug}-main`,
      name: `${companyName} Headquarters`,
      addressLine1: address.addressLine1!,
      addressLine2: address.addressLine2,
      city: address.city!,
      state: address.state!,
      zip: address.zip!,
      country: address.country!,
      county: address.county,
      phone,
      locationType: null,
      locationCode: 'LOC-001',
      timeZone: 'America/Chicago',
      status: 'Active',
      isDefault: true,
      createdAtUtc: '2026-01-01T00:00:00Z',
      updatedAtUtc: null,
      duplicateNameWarning: null,
      ...STEP5_DEFAULTS,
      taxRegistration: {
        status: 'Registered', effectiveDate: '2025-01-01', nexusEstablishedDate: '2024-12-01',
        stateAccountNumberMasked: '****0001', suiAccountNumberMasked: '****0002', sdiPflAccountNumberMasked: null,
        localTaxRegistrationMasked: null, localTaxAccountNumberMasked: null, notes: null, recordedAtUtc: '2026-01-01T00:00:00Z',
      },
      taxRegistrationStatus: 'Registered',
      registrationComplete: true,
      registrationIssues: [],
    },
    {
      id: `mock-location-${slug}-branch`,
      name: `${secondary.city} Branch`,
      addressLine1: `${200 + (hash % 800)} Demo Ave`,
      addressLine2: null,
      city: secondary.city,
      state: secondary.state,
      zip: secondary.zip,
      country: 'US',
      county: null,
      phone: `+1555555${String((hash + 1) % 10000).padStart(4, '0')}`,
      locationType: 'Branch',
      locationCode: 'LOC-002',
      timeZone: 'America/Chicago',
      status: 'Active',
      isDefault: false,
      createdAtUtc: '2026-01-05T00:00:00Z',
      updatedAtUtc: null,
      duplicateNameWarning: null,
      ...STEP5_DEFAULTS,
    },
    {
      id: `mock-location-${slug}-archived`,
      name: 'Former Satellite Office',
      addressLine1: `${300 + (hash % 700)} Old Demo Rd`,
      addressLine2: null,
      city: secondary.city,
      state: secondary.state,
      zip: secondary.zip,
      country: 'US',
      county: null,
      phone: null,
      locationType: 'Branch',
      locationCode: 'LOC-003',
      timeZone: 'America/Chicago',
      status: 'Archived',
      isDefault: false,
      createdAtUtc: '2026-01-05T00:00:00Z',
      updatedAtUtc: '2026-02-01T00:00:00Z',
      duplicateNameWarning: null,
      ...STEP5_DEFAULTS,
      activeForPayroll: false,
    },
  ];

  const members: MembershipResponse[] = [
    {
      id: `mock-membership-${slug}-owner`,
      userId: `mock-user-${slug}-owner`,
      email: `owner@${slug}.example`,
      roleId: 'role-company-admin',
      roleName: 'CompanyAdmin',
      isActive: true,
      isOwner: true,
      displayName: `${companyName} Owner`,
      officeNumber: `+1555555${String((hash + 4) % 10000).padStart(4, '0')}`,
      phone: `+1555555${String((hash + 5) % 10000).padStart(4, '0')}`,
    },
  ];

  const invitations: MembershipInvitationResponse[] = [
    {
      id: `mock-invitation-${slug}-1`,
      inviteeEmail: `${slugify(inviteeName)}@${slug}.example`,
      name: inviteeName,
      roleId: 'role-company-admin',
      isOwner: false,
      officeNumber: `+1555555${String((hash + 2) % 10000).padStart(4, '0')}`,
      phone: `+1555555${String((hash + 3) % 10000).padStart(4, '0')}`,
      status: 'Pending',
      expiresAtUtc: '2099-01-01T00:00:00Z',
    },
  ];

  return { basicInfo, address, fundingAccount, paySchedule, workLocations, members, invitations };
}
